import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Generic list-shaped skeleton — N rows of `[avatar?] [title-line] [meta-line]`.
 * Sized to land roughly where the real list will to minimize layout
 * jump when data arrives. Use for notes lists, comment threads,
 * member lists, pending-invite lists.
 */
export const SkeletonList = ({
  rows = 5,
  withAvatar = false,
  className,
}: {
  rows?: number;
  withAvatar?: boolean;
  className?: string;
}) => (
  <ul
    className={cn('divide-y rounded-md border bg-card', className)}
    role="status"
    aria-label="Loading list"
  >
    {Array.from({ length: rows }).map((_, i) => (
      <li key={i} className="flex items-center gap-3 px-4 py-3">
        {withAvatar ? <Skeleton className="size-8 shrink-0 rounded-full" /> : null}
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      </li>
    ))}
  </ul>
);

/**
 * Vertical stack of N text-line skeletons. Use for paragraph-shaped
 * loaders (page descriptions, settings sections) or as a simpler
 * fallback than `SkeletonList` when there's no list shape to mimic.
 */
export const SkeletonStack = ({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) => (
  <div className={cn('space-y-2', className)} role="status" aria-label="Loading">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className={cn(
          'h-4',
          // Subtle width variation so the stack doesn't read as a
          // perfectly-uniform block — closer to a real paragraph.
          i % 3 === 0 ? 'w-full' : i % 3 === 1 ? 'w-11/12' : 'w-3/4',
        )}
      />
    ))}
  </div>
);

/**
 * Card-shape skeleton for stat / summary tiles. Default 3 cards
 * matches `WorkspaceReportsPage`'s row of summary tiles + works for
 * `WorkspaceHomePage`'s 2-stat row by passing `count={2}`.
 */
export const SkeletonStatCards = ({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
}) => (
  <div
    className={cn('grid grid-cols-1 gap-3 sm:grid-cols-3', className)}
    role="status"
    aria-label="Loading stats"
  >
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-card rounded-lg border p-4">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="mt-3 h-7 w-1/2" />
        <Skeleton className="mt-2 h-3 w-1/4" />
      </div>
    ))}
  </div>
);
